import React, { useState, useEffect, useContext } from "react";
import { Table, Spinner, Container, Card } from "react-bootstrap";
import {
  Button,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import { getVendorFunctions, createVendorFunction, updateVendorFunction } from "./deviceService";
import { serverHost } from "variables/serverdata";

const VendorFunctionsTable = ({ vendorUuid }) => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";

  const [functions, setFunctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleteModal, setDeleteModal] = useState(false);
  const [fnToDelete, setFnToDelete] = useState(null);
  const [editModal, setEditModal] = useState(false);
  const [currentFunction, setCurrentFunction] = useState({});

  const visibleColumns = [
    { key: "type", label: "Type" },
    { key: "subtype", label: "Subtype" },
    { key: "value", label: "Value" },
    { key: "groups", label: "Groups" },
    { key: "enabled", label: "Enabled" },
    { key: "description", label: "Description" },
  ];

  const booleanColumns = ["enabled"];

  const formatBoolean = (val) => (val === true || val === "t" || val === "true" ? "Yes" : "No");

  const fetchFunctions = async () => {
    if (!vendorUuid) return;
    setLoading(true);
    try {
      const data = await getVendorFunctions(vendorUuid);
      setFunctions(data.vendor_functions || []);
    } catch (err) {
      setError(err.message || "Failed to fetch vendor functions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFunctions();
  }, [vendorUuid]);

  const emptyFunction = {
    type: "",
    subtype: "",
    value: "",
    groups: "",
    enabled: false,
    description: "",
  };

  const openAddModal = () => {
    setCurrentFunction(emptyFunction);
    setEditModal(true);
  };

  const openEditModal = (fn) => {
    setCurrentFunction({
      ...emptyFunction,
      ...fn,
      enabled: fn?.enabled === true || fn?.enabled === "t" || fn?.enabled === "true",
    });
    setEditModal(true);
  };

  const closeEditModal = () => {
    setCurrentFunction(emptyFunction);
    setEditModal(false);
  };

  const openDeleteModal = (fn) => {
    setFnToDelete(fn);
    setDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setFnToDelete(null);
    setDeleteModal(false);
  };

  const handleDelete = async () => {
    if (!fnToDelete) return;
    try {
      const res = await fetch(`${serverHost}/delete_vendor_function/${fnToDelete.device_vendor_function_uuid}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Delete failed");
      await res.json();
      closeDeleteModal();
      fetchFunctions();
    } catch (err) {
      alert(`Error deleting function: ${err.message}`);
    }
  };

  const handleSaveFunction = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    const user_uuid = user?.user_uuid || null;
    try {
      if (currentFunction.device_vendor_function_uuid) {
        // Update existing
        await updateVendorFunction(currentFunction.device_vendor_function_uuid, currentFunction, user_uuid);
      } else {
        // Create new
        await createVendorFunction(vendorUuid, currentFunction, user_uuid);
      }
      setEditModal(false);
      fetchFunctions();
    } catch (err) {
      alert(`Failed to save function: ${err.message}`);
    }
  };

  const handleChange = (field, value) => {
    setCurrentFunction((prev) => ({ ...prev, [field]: value }));
  };

  if (loading)
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" variant={isDark ? "light" : "primary"} />
        <p className="mt-2" style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
          Loading vendor functions...
        </p>
      </div>
    );

  if (error)
    return (
      <Alert variant="danger" className="mt-3 text-center">
        {error}
      </Alert>
    );

  if (!functions.length) return <p className="mt-3">No functions found for this vendor.</p>;

  return (
    <Container fluid className="mt-4">
      <Card className={`shadow-sm ${isDark ? "bg-dark text-light" : ""}`}>
        <Card.Header
          style={{
            backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
            color: isDark ? "#e2e8f0" : "#111",
          }}
          className="d-flex justify-content-between align-items-center"
        >
          <h4 className="mb-0">Vendor Functions</h4>
          <Button color="primary" size="sm" onClick={() => openAddModal()}>
            + Add Function
          </Button>
        </Card.Header>

        <Card.Body style={{ padding: 0, backgroundColor: isDark ? "#1f2937" : "#fff" }}>
          <div style={{ overflowX: "auto" }}>
            <Table
              striped
              hover
              responsive
              bordered
              className="mb-0"
              style={{
                minWidth: "900px",
                backgroundColor: isDark ? "#1f2937" : "#fff",
                color: isDark ? "#e2e8f0" : "#212529",
              }}
            >
              <thead
                style={{
                  backgroundColor: isDark ? "#111827" : "#f8f9fa",
                  fontWeight: "bold",
                  color: isDark ? "#e2e8f0" : "#212529",
                }}
              >
                <tr>
                  {visibleColumns.map((col) => (
                    <th key={col.key} className="text-center">
                      {col.label}
                    </th>
                  ))}
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {functions.map((fn) => (
                  <tr key={fn.device_vendor_function_uuid} style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                    {visibleColumns.map((col) => (
                      <td key={`${fn.device_vendor_function_uuid}-${col.key}`} className="text-center">
                        {booleanColumns.includes(col.key) ? formatBoolean(fn[col.key]) : fn[col.key] ?? "-"}
                      </td>
                    ))}
                    <td className="text-center">
                      <Button size="sm" color="primary" className="me-2" onClick={() => openEditModal(fn)}>
                        Edit
                      </Button>
                      <Button size="sm" color="danger" onClick={() => openDeleteModal(fn)}>
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {/* Edit/Add Function Modal */}
      <Modal isOpen={editModal} toggle={closeEditModal}>
        <ModalHeader
          style={{
            backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
            color: isDark ? "#e2e8f0" : "#111",
          }}
          toggle={closeEditModal}
        >
          {currentFunction.device_vendor_function_uuid ? "Edit Function" : "Add Function"}
        </ModalHeader>

        <ModalBody className="card-body">
          <Form>
            {visibleColumns.map((col) => (
              <FormGroup key={col.key}>
                <Label for={`vendor_fn_${col.key}`}>
                  {col.label}
                  {(col.key === "type" || col.key === "value") && (
                    <span className="text-danger"> *</span>
                  )}
                </Label>

                {booleanColumns.includes(col.key) ? (
                  <Input
                    type="select"
                    name={col.key}
                    id={`vendor_fn_${col.key}`}
                    value={currentFunction[col.key] ? "true" : "false"}
                    onChange={(e) => handleChange(col.key, e.target.value === "true")}
                  >
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </Input>
                ) : (
                  <Input
                    type="text"
                    name={col.key}
                    id={`vendor_fn_${col.key}`}
                    placeholder={`Enter ${col.label.toLowerCase()}`}
                    value={currentFunction[col.key] ?? ""}
                    onChange={(e) => handleChange(col.key, e.target.value)}
                  />
                )}
              </FormGroup>
            ))}
          </Form>
        </ModalBody>

        <ModalFooter className="card-body">
          <Button color="danger" onClick={closeEditModal}>
            Cancel
          </Button>
          <Button color="success" onClick={handleSaveFunction}>
            {currentFunction.device_vendor_function_uuid ? "Save Changes" : "Add Function"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal} toggle={closeDeleteModal} centered>
        <ModalHeader
          style={{
            backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
            color: isDark ? "#e2e8f0" : "#111",
          }}
          toggle={closeDeleteModal}
        >
          Delete Vendor Function
        </ModalHeader>

        <ModalBody className="card-body">
          Are you sure you want to delete this vendor function?
          <br />
          <strong>This action cannot be undone.</strong>
        </ModalBody>

        <ModalFooter className="card-body">
          <Button color="light" onClick={closeDeleteModal}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleDelete}>
            Delete
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default VendorFunctionsTable;
